import { uploadService } from '@/services/upload-service'
import Evaporate from 'evaporate'
import sparkMD5 from 'spark-md5'
import sha256 from 'js-sha256'
import _ from 'lodash'
import { normalize } from 'normalizr'
import { schemas } from '@/store/schemas'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import axios from 'axios'
import Vue from 'vue'
import {
  ADD_UPLOAD,
  CANCEL_UPLOAD,
  CREATE_FILE_UPLOAD,
  GET_CLIENT_UPLOAD_CONFIG,
  MERGE_FILE_UPLOADS,
  PAUSE_UPLOAD,
  PUT_FILE_UPLOAD,
  REMOVE_UPLOAD,
  RESUME_UPLOAD,
  SET_CLIENT_UPLOAD_CONFIG,
  SET_UPLOAD_STATUS,
  START_UPLOAD,
  UPDATE_UPLOAD_PROGRESS,
  UPLOAD_FILE
} from '@/store/mutation-types'
import { coreApiPath, clinicalApiPath } from '@/services/viper-api'

const UPLOADING = 'UPLOADING'
const COMPLETE = 'COMPLETE'
const PAUSING = 'PAUSING'
const PAUSED = 'PAUSED'
const CANCELLED = 'CANCELLED'
const ERROR = 'ERROR'

let evaporate = null

export const namespaced = false
export const state = {
  fileUploads: {},
  currentUploads: {},
  clientUploadConfig: null
}

export const mutations = {
  [MERGE_FILE_UPLOADS](state, fileUploads) {
    state.fileUploads = _.merge({}, state.fileUploads, fileUploads)
  },
  [ADD_UPLOAD](state, upload) {
    state.currentUploads = _.merge({}, state.currentUploads, upload)
  },
  [START_UPLOAD](state, { key, fileKey }) {
    state.currentUploads[key].status = UPLOADING
    state.currentUploads[key].fileKey = fileKey
  },
  [REMOVE_UPLOAD](state, key) {
    Vue.delete(state.currentUploads, key)
  },
  [SET_UPLOAD_STATUS](state, { key, status }) {
    state.currentUploads[key].status = status
  },
  [UPDATE_UPLOAD_PROGRESS](state, { key, stats, percent }) {
    state.currentUploads[key].stats = stats
    state.currentUploads[key].percent = percent
  },
  [SET_CLIENT_UPLOAD_CONFIG](state, clientUploadConfig) {
    state.clientUploadConfig = clientUploadConfig
  }
}

async function createEvaporateInstance(state) {
  evaporate = await Evaporate.create({
    signerUrl: `${window.$appConfig.VUE_APP_VIPER_API_BASE_URL}${coreApiPath}${clinicalApiPath}/upload/auth`,
    sendCanonicalRequestToSignerUrl: true,
    aws_key: state.clientUploadConfig.uploadPublicKey,
    bucket: state.clientUploadConfig.uploadBucketName,
    progressIntervalMS: 250,
    logging: false,
    computeContentMd5: true,
    cryptoMd5Method: d => btoa(sparkMD5.ArrayBuffer.hash(d, true)),
    cryptoHexEncodedHash256: sha256,
    s3Acceleration: state.clientUploadConfig.s3Acceleration,
    s3FileCacheHoursAgo: state.clientUploadConfig.s3FileCacheHoursAgo,
    onlyRetryForSameFileName: state.clientUploadConfig.onlyRetryForSameFileName,
    signHeaders: {
      Authorization: function() {
        return `Bearer ${Vue.prototype.$keycloak.token}`
      }
    }
  })
}

async function singlePartUpload(commit, state, dispatch, file, fileUpload) {
  const stats = {}
  const key = fileUpload.id
  const path = fileUpload.url
  const config = {
    onUploadProgress: progressEvent => {
      const percent = (progressEvent.loaded / progressEvent.total) * 100
      commit(UPDATE_UPLOAD_PROGRESS, { key, stats, percent })
    },
    headers: { 'Content-Type': 'binary/octet-stream' }
  }

  commit(START_UPLOAD, { key, fileKey: key })

  let heartbeatInterval = setInterval(() => {
    const fileUpload = state.fileUploads[key]
    fileUpload.uploadStatus = state.currentUploads[key].status
    if (!fileUpload.startTime) {
      fileUpload.startTime = Date.now()
    }
    dispatch(PUT_FILE_UPLOAD, fileUpload)
  }, 10000)

  try {
    await axios.put(path, file, config)
    const fileUpload = state.fileUploads[key]
    fileUpload.uploadStatus = COMPLETE
    fileUpload.endTime = Date.now()
    return dispatch(PUT_FILE_UPLOAD, fileUpload)
  } catch (error) {
    const fileUpload = state.fileUploads[key]
    fileUpload.uploadStatus = state.currentUploads[key].status
    dispatch(PUT_FILE_UPLOAD, fileUpload)
    console.warn('File did not upload successfully:', error)
    throw error
  } finally {
    clearInterval(heartbeatInterval)
    commit(REMOVE_UPLOAD, key)
  }
}

async function multipartUpload(commit, state, dispatch, file, fileUpload) {
  if (!evaporate) {
    await createEvaporateInstance(state)
  }
  let heartbeatInterval
  const key = fileUpload.id
  let completeFileUploadPromise
  try {
    return await evaporate.add({
      file: file,
      name: key,
      progress: (percent, stats) => {
        commit(UPDATE_UPLOAD_PROGRESS, { key, stats, percent })
      },
      complete: () => {
        commit(SET_UPLOAD_STATUS, { key, status: COMPLETE })
        const fileUpload = state.fileUploads[key]
        fileUpload.uploadStatus = COMPLETE
        fileUpload.endTime = Date.now()
        completeFileUploadPromise = dispatch(PUT_FILE_UPLOAD, fileUpload)
      },
      error: mssg => {
        commit(SET_UPLOAD_STATUS, { key, status: ERROR })
        const fileUpload = state.fileUploads[key]
        fileUpload.uploadStatus = ERROR
        dispatch(PUT_FILE_UPLOAD, fileUpload)
        throw Error(mssg)
      },
      paused: () => {
        commit(SET_UPLOAD_STATUS, { key, status: PAUSED })
      },
      pausing: () => {
        commit(SET_UPLOAD_STATUS, { key, status: PAUSING })
      },
      resumed: () => {
        commit(SET_UPLOAD_STATUS, { key, status: UPLOADING })
      },
      cancelled: () => {
        commit(SET_UPLOAD_STATUS, { key, status: CANCELLED })
      },
      started: fileKey => {
        commit(START_UPLOAD, { key, fileKey })
        heartbeatInterval = setInterval(() => {
          const fileUpload = state.fileUploads[key]
          fileUpload.uploadStatus = UPLOADING
          fileUpload.avgSpeedBytesPerSecond = state.currentUploads[key].stats.speed
          fileUpload.secondsLeft = state.currentUploads[key].stats.secondsLeft
          fileUpload.bytesUploaded = state.currentUploads[key].stats.totalUploaded
          fileUpload.uploadStatus = state.currentUploads[key].status
          if (!fileUpload.startTime) {
            fileUpload.startTime = Date.now()
          }
          dispatch(PUT_FILE_UPLOAD, fileUpload)
        }, 10000)
      },
      warn: msg => console.warn('Warning', msg)
    })
  } finally {
    clearInterval(heartbeatInterval)
    await completeFileUploadPromise
    commit(REMOVE_UPLOAD, key)
  }
}

export const actions = {
  async [CREATE_FILE_UPLOAD]({ commit }, fileUpload) {
    const response = await uploadService.createFileUpload(fileUpload)
    const normalized = normalize(response.data, schemas.fileUpload)
    commit(MERGE_FILE_UPLOADS, normalized.entities.fileUploads)
    return response
  },
  async [UPLOAD_FILE]({ commit, state, dispatch }, { file, fileUpload }) {
    if (!state.clientUploadConfig) {
      await dispatch(GET_CLIENT_UPLOAD_CONFIG)
    }

    const upload = {
      [fileUpload.id]: {
        percent: 0.0,
        stats: {},
        status: 'QUEUED',
        fileKey: '',
        startTime: null,
        endTime: null
      }
    }
    commit(ADD_UPLOAD, upload)

    if (state.clientUploadConfig.multipartUpload === true) {
      return await multipartUpload(commit, state, dispatch, file, fileUpload)
    } else {
      return await singlePartUpload(commit, state, dispatch, file, fileUpload)
    }
  },
  async [PAUSE_UPLOAD]({ state }, fileUploadId) {
    if (state.clientUploadConfig.multipartUpload === true) {
      return evaporate.pause([state.currentUploads[fileUploadId].fileKey])
    }
  },
  async [CANCEL_UPLOAD]({ state }, fileUploadId) {
    if (state.clientUploadConfig.multipartUpload === true) {
      return evaporate.cancel([state.currentUploads[fileUploadId].fileKey])
    }
  },
  async [RESUME_UPLOAD]({ state }, fileUploadId) {
    if (state.clientUploadConfig.multipartUpload === true) {
      return evaporate.resume([state.currentUploads[fileUploadId].fileKey])
    }
  },
  async [PUT_FILE_UPLOAD]({ commit }, fileUpload) {
    fileUpload.organization = MyActiveOrganization
    const response = await uploadService.putFileUpload(fileUpload)
    const normalized = normalize(response.data, schemas.fileUpload)
    commit(MERGE_FILE_UPLOADS, normalized.entities.fileUploads)
    return response
  },
  async [GET_CLIENT_UPLOAD_CONFIG]({ commit }) {
    const response = await uploadService.getUploadConfig()
    commit(SET_CLIENT_UPLOAD_CONFIG, response.data)
  }
}
